import { useCallback, useMemo, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import { Button, DataBlock, PageSection } from '@/common'
import { CalculatorIcon } from '@/common/components/icons'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import EuaEmissionPriceOverviewWidget from '@/features/cbam-reporting/components/EuaEmissionPriceOverviewWidget'
import CertificatesAndLiabilityPerQuarter from '@/features/cbam-simulations-v2/pages/cbam-simulation-details/components/CertificatesAndLiabilityPerQuarter'
import { getSimulationYearTotals } from '@/features/cbam-simulations-v2/pages/cbam-simulation-details/utils/simulationYearTotals'
import {
  type CbamReport,
  useUpdateCbamReportModifiers
} from '@/features/cbam-reporting/hooks/cbamReports'
import type { BatchLiabilityResponse } from '@/features/cbam-reporting/hooks/cbamLiability'
import type { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { getDefaultCbamModifiers } from '@/shared/types/cbamModifiers'
import EditCbamModifiersDrawer from '@/shared/components/EditCbamModifiersDrawer'
import { QUARTERS, type Quarter } from '@/config/cbamReportingPeriods'
import CbamReportScenariosTotalsRow from './CbamReportScenariosTotalsRow'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'

type GoodRow = ImportedGood & { emissionsReport: SupplierEmissionsReport | null }

const SUMMARY_NS = 'cbamReporting:cbamReportDetails.estimator.summaryTotals'

type Props = {
  reportingPeriodYear: number
  cbamReport: CbamReport
  modifiers: CbamModifiers
  liabilityDataBaseLow: BatchLiabilityResponse | undefined
  liabilityDataHighForScenario: BatchLiabilityResponse | undefined
  isBatchLiabilityLoading: boolean
  /** Goods matching the current filters (subset of the report). */
  goods: GoodRow[]
  /** All goods in the report (unfiltered); used with `goods` for “filtered of total”. */
  totalGoodsInReport: number
  isEditVariablesOpen: boolean
  openEditVariables: () => void
  closeEditVariables: () => void
  /** Rendered after the summary grid and before certificates per quarter (e.g. `ImportedGoodsSection`). */
  children?: ReactNode
}

export default function CbamReportLiabilityEstimatorLayout({
  reportingPeriodYear,
  cbamReport,
  modifiers,
  liabilityDataBaseLow,
  liabilityDataHighForScenario,
  isBatchLiabilityLoading,
  goods,
  totalGoodsInReport,
  isEditVariablesOpen,
  openEditVariables,
  closeEditVariables,
  children
}: Props) {
  const { t, numberFormatter3Decimals } = useI18n()
  const { formatNetMass } = useNetMassDisplay()
  const organizationId = useSession().session.organization.id
  const { mutateAsync: updateCbamReportModifiers, isPending: isUpdatingModifiers } =
    useUpdateCbamReportModifiers(organizationId, cbamReport.id)

  const reportInitialModifiers = useMemo(
    () => cbamReport.modifiers ?? getDefaultCbamModifiers(),
    [cbamReport]
  )

  const handleSaveReportModifiers = useCallback(
    async (nextModifiers: CbamModifiers) => {
      try {
        await updateCbamReportModifiers({ modifiers: nextModifiers })
        toast.success(t('cbamReporting:cbamReportDetails.estimator.notifications.modifiersUpdated'))
      } catch (error) {
        console.error(error)
        toast.error(
          t('cbamReporting:cbamReportDetails.estimator.notifications.modifiersUpdateError')
        )
        throw error
      }
    },
    [t, updateCbamReportModifiers]
  )

  const formatDirectEmissions = (value: number) =>
    value > 0 ? `${numberFormatter3Decimals(value)} tCO₂e` : '–'
  const quartersWithGoods = useMemo(() => {
    const quarters = new Set<Quarter>()
    for (const good of goods) {
      if (good.quarter != null && QUARTERS.includes(good.quarter)) {
        quarters.add(good.quarter)
      }
    }
    return Array.from(quarters)
  }, [goods])

  const yearTotals = useMemo(
    () =>
      getSimulationYearTotals(
        liabilityDataBaseLow,
        modifiers,
        reportingPeriodYear,
        quartersWithGoods,
        liabilityDataHighForScenario
      ),
    [
      liabilityDataBaseLow,
      liabilityDataHighForScenario,
      modifiers,
      reportingPeriodYear,
      quartersWithGoods
    ]
  )

  const cscfParsed = parseFloat(modifiers.cscfPercentage)
  const cscfPercentageFromModifiers =
    !Number.isNaN(cscfParsed) && cscfParsed !== 100 ? cscfParsed : undefined

  const editVariablesTooltip = cbamReport.isCompleted
    ? t('cbamReporting:cbamReportDetails.estimator.editVariables.editVariablesDisabledTooltip')
    : undefined

  return (
    <>
      <CbamReportScenariosTotalsRow
        totalCertificates={yearTotals.totalCertificates}
        totalLiability={yearTotals.totalLiabilityBase}
        totalCertificatesHigh={yearTotals.totalCertificatesHigh}
        cscfPercentageFromModifiers={cscfPercentageFromModifiers}
        totalLiabilityLow={yearTotals.totalLiabilityLow}
        totalLiabilityHigh={yearTotals.totalLiabilityHigh}
      />
      <div className="grid grid-cols-4 gap-6">
        <div className="flex flex-col gap-2">
          <DataBlock
            label={t(`${SUMMARY_NS}.totalEmissions`)}
            data={formatDirectEmissions(yearTotals.totalDirectEmissions)}
          />
          <DataBlock
            label={t(`${SUMMARY_NS}.totalVolume`)}
            data={formatNetMass(yearTotals.totalVolume)}
          />
          <DataBlock
            label={t(`${SUMMARY_NS}.totalGoods`)}
            data={
              totalGoodsInReport > 0 ? (
                <span className="inline-flex flex-wrap items-baseline gap-x-1">
                  <span>{goods.length}</span>
                  <span className="text-sm font-normal text-gray-500">
                    {t(`${SUMMARY_NS}.totalGoodsOfTotalSuffix`, {
                      total: totalGoodsInReport
                    })}
                  </span>
                </span>
              ) : (
                '–'
              )
            }
          />
        </div>
        <div className="col-span-3">
          <EuaEmissionPriceOverviewWidget toggleButtonSize="xs" />
        </div>
      </div>

      {children}

      <PageSection
        title={t('cbamModifiers:certificatesAndLiabilityPerQuarter.sectionTitle')}
        actions={
          <Button
            icon={<CalculatorIcon />}
            onClick={openEditVariables}
            disabled={cbamReport.isCompleted}
            tooltip={editVariablesTooltip}
          >
            {t('cbamReporting:cbamReportDetails.estimator.editVariables.button')}
          </Button>
        }
      >
        <CertificatesAndLiabilityPerQuarter
          modifiers={modifiers}
          simulationYear={reportingPeriodYear}
          quartersWithGoods={quartersWithGoods}
          aggregatedProjectionByQuarter={liabilityDataBaseLow?.aggregatedProjectionByQuarter}
          aggregatedProjectionByQuarterHigh={
            liabilityDataHighForScenario?.aggregatedProjectionByQuarter
          }
          isLoading={isBatchLiabilityLoading}
        />
      </PageSection>

      <EditCbamModifiersDrawer
        isOpen={isEditVariablesOpen}
        close={closeEditVariables}
        year={reportingPeriodYear}
        initialModifiers={reportInitialModifiers}
        syncSource={cbamReport}
        isSaving={isUpdatingModifiers}
        onSave={handleSaveReportModifiers}
      />
    </>
  )
}
